import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Info, ShieldCheck, Undo2, Users } from 'lucide-react';
import { Button } from '../../Button';
import { Modal } from '../../common/Modal';
import { Panel } from '../../common/Panel';
import { PersonPhoto } from '../../common/PersonPhoto';
import { StatusBadge } from '../../common/StatusBadge';
import { InfoItem } from '../../common/InfoItem';
import { useApplicationReview } from '../../../contexts/ApplicationReviewContext';
import { getDriver } from '../../../data/drivers';
import { can } from '../../../utils/applicationQueue';
import type { Driver, Tone } from '../../../types/domain';
import type { OwnerApplication } from '../../../types/owner';
import type { DriverDecision } from '../../../types/review';
const decisionMeta: Record<DriverDecision, {
  label: string;
  tone: Tone;
}> = {
  pending: {
    label: 'সংযুক্তি যাচাই অপেক্ষমাণ',
    tone: 'orange'
  },
  approved: {
    label: 'সংযুক্তি অনুমোদিত',
    tone: 'green'
  },
  returned: {
    label: 'সংশোধনের জন্য ফেরত',
    tone: 'red'
  }
};
export function DriverReviewTab({
  app
}: {
  app: OwnerApplication;
}) {
  const {
    getReview,
    decideDriver,
    driverIdsFor,
    role
  } = useApplicationReview();
  const [open, setOpen] = useState<Driver | null>(null);
  const [checked, setChecked] = useState(false);
  const [note, setNote] = useState('');
  const r = getReview(app);
  const drivers = driverIdsFor(app).map(id => getDriver(id)).filter((d): d is Driver => Boolean(d));
  const allowed = can(role, 'driver');
  if (app.vehicleType !== 'cng') {
    return <Panel data-id="element-381" data-mp="2t2lolakzbw81"><p className="flex items-center gap-2 text-sm text-slate-600" data-id="element-382" data-mp="2t2ll30lrte3a"><Info className="h-4 w-4" data-id="element-383" data-mp="2y0dey05z7aa4" />মোটরসাইকেল আবেদনে চালক সংযুক্তি প্রযোজ্য নয় — মালিক নিজেই রাইডার।</p></Panel>;
  }
  const openDriver = (d: Driver) => {
    setOpen(d);
    setChecked(r.drivers[d.id] === 'approved');
    setNote(r.driverNotes[d.id] ?? '');
  };
  const decide = (decision: DriverDecision) => {
    if (!open) return;
    if (decision === 'returned' && note.trim().length < 4) return toast.error('ফেরতের কারণ লিখুন');
    decideDriver(app, open.id, decision, note);
    toast.success(decision === 'approved' ? `${open.name} — চালক সংযুক্তি অনুমোদিত` : `${open.name} — সংশোধনের জন্য ফেরত`);
    setOpen(null);
  };
  return <div className="space-y-4" data-id="element-384" data-mp="2fxg591funr5h">
      <p className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-900" data-id="element-385" data-mp="2fx6kptdkqc76">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" data-id="element-386" data-mp="2tvmuxuanaku2" />
        গাড়ি অনুমোদিত হলেও চালক সংযুক্তি স্বয়ংক্রিয়ভাবে অনুমোদিত হয় না — প্রতিটি চালকের লাইসেন্স আলাদাভাবে যাচাই করতে হবে।
      </p>
      <Panel title="সংযুক্ত চালকের তথ্য" subtitle={`${drivers.length.toLocaleString('bn-BD')} জন প্রস্তাবিত/অনুমোদিত চালক • বিস্তারিত দেখতে কার্ডে ক্লিক করুন`} data-id="element-387" data-mp="33mhgewg2hgeg">
        {drivers.length === 0 ? <p className="flex items-center gap-2 py-6 text-sm text-slate-500" data-id="element-388" data-mp="3co8lbz2s9j79"><Users className="h-5 w-5 text-slate-300" data-id="element-389" data-mp="37p4bd681bhfv" />এই আবেদনে কোনো চালক প্রস্তাব করা হয়নি</p> : <div className="grid gap-3 md:grid-cols-3" data-id="element-390" data-mp="3ra4hl9xabp09">
            {drivers.map((d, i) => {
          const st = decisionMeta[r.drivers[d.id] ?? 'pending'];
          return <button key={d.id} onClick={() => openDriver(d)} className="flex flex-col rounded-lg border border-border p-4 text-left transition hover:border-navy-300 hover:shadow" data-id="element-391" data-mp="3ia9591rztnyn" data-mpi={i}>
                  <div className="flex items-center gap-3" data-id="element-392" data-mp="38k6e774fmk2y">
                    <PersonPhoto src={d.photo} name={d.name} size={52} rounded="md" data-id="element-393" data-mp="38ilk1im1535m" />
                    <div className="min-w-0" data-id="element-394" data-mp="3dc7mkhyhbqnp">
                      <p className="text-[11px] font-semibold text-slate-400" data-id="element-395" data-mp="2ym0d2rqg9hz5">চালক {(i + 1).toLocaleString('bn-BD')}</p>
                      <p className="font-semibold text-navy-900" data-id="element-396" data-mp="33k1vrbjyrfa3">{d.name}</p>
                      <p className="font-mono text-xs text-slate-500" data-id="element-397" data-mp="2pl2a8crqzgi5">লাইসেন্স: {d.license}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-398" data-mp="2uitymwt6okhl">
                    <StatusBadge tone={d.status === 'সক্রিয়' ? 'green' : 'red'} data-id="element-399" data-mp="2mjp90cv6jbjk">অবস্থা: {d.status}</StatusBadge>
                    <StatusBadge tone={st.tone} data-id="element-400" data-mp="2hle0q6kj993g">{st.label}</StatusBadge>
                  </div>
                </button>;
        })}
          </div>}
      </Panel>

      <Modal open={Boolean(open)} onClose={() => setOpen(null)} title={open ? `চালকের বিস্তারিত — ${open.name}` : ''} width="max-w-2xl" footer={allowed && <>
              <Button variant="outline" onClick={() => decide('returned')} className="border-amber-300 text-amber-800" data-id="element-402" data-mp="1ysa4qejuek5o"><Undo2 className="h-4 w-4" data-id="element-403" data-mp="1tva5onleuho2" />সংশোধনের জন্য ফেরত</Button>
              <Button onClick={() => decide('approved')} disabled={!checked} className="bg-emerald-600 hover:bg-emerald-700" data-id="element-404" data-mp="1pu87ma38cbno"><CheckCircle2 className="h-4 w-4" data-id="element-405" data-mp="3o7hynve68oh7" />অনুমোদন</Button>
            </>} data-id="element-401" data-mp="28su9vsahg8t6">
        {open && <div className="space-y-4" data-id="element-406" data-mp="3k7zgh7dqiri2">
            <div className="flex gap-4" data-id="element-407" data-mp="15zoswzvy6mdm">
              <PersonPhoto src={open.photo} name={open.name} size={96} rounded="md" data-id="element-408" data-mp="15yx54b8ldsxw" />
              <dl className="grid flex-1 gap-x-4 gap-y-3 sm:grid-cols-2" data-id="element-409" data-mp="1axhxjohlr8bs">
                <InfoItem label="লাইসেন্স নম্বর" value={open.license} mono data-id="element-410" data-mp="1foq6kjf0srof" />
                <InfoItem label="লাইসেন্সের ধরন" value={open.licenseType} data-id="element-411" data-mp="1jqttc4753ip0" />
                <InfoItem label="লাইসেন্সের মেয়াদ" value={open.expiry} data-id="element-412" data-mp="1jsxw2itaybnp" />
                <InfoItem label="মোবাইল" value={open.mobile} data-id="element-413" data-mp="n1vx0hjqrcpq" />
                <InfoItem label="BRTA লাইসেন্স যাচাই" value={open.brtaVerified ? 'যাচাইকৃত (সিমুলেটেড)' : 'অপেক্ষমাণ'} data-id="element-414" data-mp="rze1nvqq75rx" />
                <InfoItem label="সংযুক্তির অবস্থা" value={<StatusBadge tone={decisionMeta[r.drivers[open.id] ?? 'pending'].tone} data-id="element-416" data-mp="w3dbjvhi36cb">{decisionMeta[r.drivers[open.id] ?? 'pending'].label}</StatusBadge>} data-id="element-415" data-mp="w07mgg8vg4gl" />
              </dl>
            </div>
            {allowed ? <>
                <label className={`flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2.5 text-sm ${checked ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'}`} data-id="element-417" data-mp="2cezeut60pbpw">
                  <input type="checkbox" checked={checked} onChange={() => setChecked(c => !c)} className="h-4 w-4 accent-emerald-600" data-id="element-418" data-mp="8bxuua2dz09m" />
                  <span className="font-medium text-navy-900" data-id="element-419" data-mp="ma49m7vn89jf">চালকের তথ্য যাচাই — লাইসেন্স, ধরন ও মেয়াদ BRTA রেকর্ডের সাথে মিলিয়ে দেখা হয়েছে</span>
                </label>
                <div data-id="element-420" data-mp="3prmhgfbf1gju">
                  <label htmlFor="drv-note" className="mb-1 block text-xs font-medium text-slate-600" data-id="element-421" data-mp="3pt77psqp6dbw">মন্তব্য / ফেরতের কারণ</label>
                  <textarea id="drv-note" rows={2} value={note} onChange={e => setNote(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100" data-id="element-422" data-mp="3kqzt0vehgj5v" />
                </div>
              </> : <p className="rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-600" data-id="element-423" data-mp="36zl8ul8uabx8">আপনার বর্তমান ভূমিকায় চালক সংযুক্তি অনুমোদনের অনুমতি নেই।</p>}
          </div>}
      </Modal>
    </div>;
}